import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Navbar } from './components/Navbar';
import { Sidebar } from './components/Sidebar';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { DocumentViewerPage } from './pages/DocumentViewerPage';
import { PolicyManagerPage } from './pages/PolicyManagerPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { AuditCenterPage } from './pages/AuditCenterPage';
import { AiProvidersPage } from './pages/AiProvidersPage';
import { ValidationRuleManagerPage } from './pages/ValidationRuleManagerPage';
import { PiiRuleManagerPage } from './pages/PiiRuleManagerPage';
import { ComplianceDashboardPage } from './pages/ComplianceDashboardPage';
import { LandingPage } from './pages/LandingPage';
import { OverviewPage } from './pages/OverviewPage';
import { CallbackPage } from './pages/CallbackPage';
import { SubscriptionPage } from './pages/SubscriptionPage';

import { AccessBlockedPage } from './pages/AccessBlockedPage';

import { JobsByUserPage } from './pages/JobsByUserPage';

const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSuperAdmin } = useAuth();
  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

const ProtectedLayout: React.FC = () => {
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col font-sans">
      <Navbar
        mobileMenuOpen={mobileMenuOpen}
        onToggleMobileMenu={() => setMobileMenuOpen(!mobileMenuOpen)}
      />
      <div className="flex flex-1 relative">
        <Sidebar
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        />
        <main className="flex-1 p-4 sm:p-6 md:p-8 overflow-y-auto max-w-7xl mx-auto w-full min-w-0">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/providers" element={<AiProvidersPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/documents/:id" element={<DocumentViewerPage />} />

            {/* Super Admin Only Routes */}
            <Route path="/overview" element={<SuperAdminRoute><OverviewPage /></SuperAdminRoute>} />
            <Route path="/jobs-by-user" element={<SuperAdminRoute><JobsByUserPage /></SuperAdminRoute>} />
            <Route path="/users" element={<SuperAdminRoute><UserManagementPage /></SuperAdminRoute>} />
            <Route path="/audit" element={<SuperAdminRoute><AuditCenterPage /></SuperAdminRoute>} />
            <Route path="/validation-rules" element={<SuperAdminRoute><ValidationRuleManagerPage /></SuperAdminRoute>} />
            <Route path="/pii-rules" element={<SuperAdminRoute><PiiRuleManagerPage /></SuperAdminRoute>} />
            <Route path="/compliance" element={<SuperAdminRoute><ComplianceDashboardPage /></SuperAdminRoute>} />
            <Route path="/policies" element={<SuperAdminRoute><PolicyManagerPage /></SuperAdminRoute>} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/callback" element={<CallbackPage />} />
          <Route path="/*" element={<ProtectedLayout />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};
